import React, { useState, useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSTextField from "../../system-design/DSTextField";
import DSBox from "../../system-design/DSBox";
import DSGrid from "../../system-design/DSGrid";
import DSFormControl from "../../system-design/DSFormControl";
import DSInputLabel from "../../system-design/DSInputLabel";
import DSSelect from "../../system-design/DSSelect";
import DSMenuItem from "../../system-design/DSMenuItem";
import DSSwitch from "../../system-design/DSSwitch";
import DSTypography from "../../system-design/DSTypography";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSAvatar from "../../system-design/DSAvatar";
import DSIconButton from "../../system-design/DSIconButton";
import DSChip from "../../system-design/DSChip";
import DeleteIcon from "@mui/icons-material/Delete";
import { Close } from "@mui/icons-material";
import {
  CalendarEventsType,
  KLYDO_EVENT_PRIMARY_TYPE,
  KlydoPickMethod,
  GeographicRegion,
  State,
} from "../../../api/calendarEvent";
import { Klydo } from "../../../Types";
import klydoApiClient from "../../../api/klydo";
import calendarEventApiClient from "../../../api/calendarEvent";
import PublicKlydosPickDialog from "../../Gifts/GiftCategories/components/PublicKlydosPickDialog";
import KlydoPickMethodSelect from "./KlydoPickMethodSelect";
import KlydoLoopUrl from "../../Klydos/components/KlydoLoopUrl";
import CountryDropdown from "./CountryDropdown";
import StateDropdown from "./StateDropdown";
import ScheduledDatePicker from "./ScheduledDatePicker";
import useKlydoEventForDate from "../hooks/useKlydoEventForDate";
import StringChipEditor from "../../../components/Widgets/StringChipEditor";
import ObjectID from "bson-objectid";
import { areStatesEqual } from "../../../utils/util";

const ALL_PRIMARY_TYPES = [
  ...Object.values(CalendarEventsType),
  KLYDO_EVENT_PRIMARY_TYPE,
];

const formatKlydoDefaultName = (d: Date): string => {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `Daily Klydo - ${mm}/${dd}/${yy}`;
};

interface UnifiedCalendarEventModalProps {
  open: boolean;
  onClose: () => void;
  initialDate?: Date;
  initialKlydoIds?: string[];
  initialPrimaryType?: string;
  initialName?: string;
  minDate?: Date;
  onKlydoClick?: (klydo: Klydo) => void;
}

const UnifiedCalendarEventModal = ({
  open,
  onClose,
  initialDate,
  initialKlydoIds,
  initialPrimaryType,
  initialName,
  minDate,
  onKlydoClick,
}: UnifiedCalendarEventModalProps) => {
  const queryClient = useQueryClient();
  const today = new Date();
  const defaultDate =
    initialDate && (!minDate || initialDate >= minDate)
      ? initialDate
      : minDate || today;

  const [primaryType, setPrimaryType] = useState(
    initialPrimaryType || KLYDO_EVENT_PRIMARY_TYPE,
  );
  const isInitiallyKlydoEvent =
    (initialPrimaryType || KLYDO_EVENT_PRIMARY_TYPE) ===
    KLYDO_EVENT_PRIMARY_TYPE;

  const [name, setName] = useState(
    initialName ||
      (isInitiallyKlydoEvent ? formatKlydoDefaultName(defaultDate) : ""),
  );
  const [description, setDescription] = useState("");
  const [selectedDate, setSelectedDate] = useState<Date>(defaultDate);
  const [country, setCountry] = useState<GeographicRegion | null>(null);
  const [states, setStates] = useState<State[]>([]);
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const [ignoreEvent, setIgnoreEvent] = useState(false);
  const [selectedKlydoIds, setSelectedKlydoIds] = useState<string[]>(
    initialKlydoIds || [],
  );
  const [pickMethod, setPickMethod] = useState<KlydoPickMethod | "">("");
  const [overrideOtherEvents, setOverrideOtherEvents] = useState(false);
  const [resolvedKlydos, setResolvedKlydos] = useState<Klydo[]>([]);
  const [isLoadingKlydos, setIsLoadingKlydos] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPickDialog, setShowPickDialog] = useState(false);

  const isKlydoEvent = primaryType === KLYDO_EVENT_PRIMARY_TYPE;

  const dayConflictQuery = useKlydoEventForDate(
    selectedDate,
    open && isKlydoEvent,
  );
  const existingKlydoEvent = dayConflictQuery.data ?? null;
  const hasDayConflict = isKlydoEvent && !!existingKlydoEvent;
  const isCheckingDayConflict = isKlydoEvent && dayConflictQuery.isFetching;

  useEffect(() => {
    let isCurrent = true;
    if (selectedKlydoIds.length > 0) {
      setIsLoadingKlydos(true);
      klydoApiClient
        .getKlydosByIds(selectedKlydoIds)
        .then((klydos) => {
          if (isCurrent) setResolvedKlydos(klydos);
        })
        .catch(() => {
          if (isCurrent) setResolvedKlydos([]);
        })
        .finally(() => {
          if (isCurrent) setIsLoadingKlydos(false);
        });
    } else {
      setResolvedKlydos([]);
    }
    return () => {
      isCurrent = false;
    };
  }, [selectedKlydoIds]);

  const getEventDate = () => ({
    year: selectedDate.getFullYear(),
    month: selectedDate.getMonth() + 1,
    day: selectedDate.getDate(),
  });

  const handleStateSelect = (state: State | null) => {
    if (state && !states.some((s) => areStatesEqual(s, state))) {
      setStates((prev) => [...prev, state]);
    }
  };

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const eventDate = getEventDate();
      const dateStr = `${eventDate.year}-${String(eventDate.month).padStart(2, "0")}-${String(eventDate.day).padStart(2, "0")}`;
      const eventId = isKlydoEvent
        ? `daily-klydo-${dateStr}`
        : new ObjectID().toHexString();
      const eventName =
        name || (isKlydoEvent ? formatKlydoDefaultName(selectedDate) : "");

      await calendarEventApiClient.upsertCalendarEvent(eventId, {
        name: eventName,
        description: description || undefined,
        primaryType,
        type: eventTypes.length > 0 ? eventTypes : [primaryType],
        country: isKlydoEvent
          ? country || { name: "Global", code: "GLOBAL" }
          : country || undefined,
        states,
        date: eventDate,
        ignoreEvent: ignoreEvent || undefined,
        ...(isKlydoEvent && {
          klydoIds: selectedKlydoIds,
          pickMethod: pickMethod || undefined,
          overrideOtherEvents,
        }),
      });

      queryClient.invalidateQueries({ queryKey: ["calendar-events"] });
      queryClient.invalidateQueries({ queryKey: ["klydo-events"] });
      queryClient.invalidateQueries({ queryKey: ["calendar-events-by-date"] });
      // The klydo modal's "already in scheduler" caption reads this key, so
      // without it a klydo just added from that modal still renders as unscheduled.
      selectedKlydoIds.forEach((selectedKlydoId) =>
        queryClient.invalidateQueries({
          queryKey: ["klydo-scheduled-event", selectedKlydoId],
        }),
      );
      onClose();
    } finally {
      setIsSaving(false);
    }
  }, [
    isKlydoEvent,
    name,
    description,
    primaryType,
    eventTypes,
    selectedDate,
    country,
    states,
    ignoreEvent,
    selectedKlydoIds,
    pickMethod,
    overrideOtherEvents,
    onClose,
    queryClient,
  ]);

  const isValid =
    name.trim() !== "" &&
    (!isKlydoEvent || (!!pickMethod && selectedKlydoIds.length > 0)) &&
    !hasDayConflict &&
    !isCheckingDayConflict;

  return (
    <>
      <DSDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DSDialogTitle>Add Calendar Event</DSDialogTitle>
        <DSDialogContent dividers>
          <DSBox
            sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
          >
            <DSFormControl fullWidth size="small">
              <DSInputLabel>Event Type</DSInputLabel>
              <DSSelect
                value={primaryType}
                label="Event Type"
                onChange={(e) => {
                  const newType = e.target.value as string;
                  setPrimaryType(newType);
                  if (newType === KLYDO_EVENT_PRIMARY_TYPE && !name) {
                    setName(formatKlydoDefaultName(selectedDate));
                  }
                }}
              >
                {ALL_PRIMARY_TYPES.map((t) => (
                  <DSMenuItem key={t} value={t}>
                    {t}
                  </DSMenuItem>
                ))}
              </DSSelect>
            </DSFormControl>

            <DSTextField
              label="Event Name"
              value={name}
              onChange={(changeEvent) => setName(changeEvent.target.value)}
              fullWidth
              size="small"
              required
            />

            <DSTextField
              label="Description"
              value={description}
              onChange={(changeEvent) =>
                setDescription(changeEvent.target.value)
              }
              fullWidth
              size="small"
              multiline
              rows={2}
            />

            <ScheduledDatePicker
              label="Date"
              value={selectedDate}
              minDate={minDate}
              country={country?.code}
              onChange={(chosen) => {
                if (minDate && chosen < minDate) return;
                setSelectedDate(chosen);
                if (isKlydoEvent) {
                  setName((prev) =>
                    prev === formatKlydoDefaultName(selectedDate)
                      ? formatKlydoDefaultName(chosen)
                      : prev,
                  );
                }
              }}
            />

            {hasDayConflict && existingKlydoEvent && (
              <DSTypography variant="caption" sx={{ color: "error.main" }}>
                {`A Klydo Event already exists on ${String(
                  existingKlydoEvent.date.day,
                ).padStart(2, "0")}/${String(
                  existingKlydoEvent.date.month,
                ).padStart(2, "0")}/${existingKlydoEvent.date.year}: "${
                  existingKlydoEvent.name
                }". Pick another date.`}
              </DSTypography>
            )}

            <CountryDropdown initialCountry={country} onSelect={setCountry} />

            <DSBox>
              <StateDropdown
                country={country}
                onSelect={handleStateSelect}
                disabled={!country}
              />
              <DSBox sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                {states.map((state) => (
                  <DSChip
                    key={state.iso}
                    label={`${state.name} (${state.iso})`}
                    onDelete={() =>
                      setStates((prev) =>
                        prev.filter((s) => !areStatesEqual(s, state)),
                      )
                    }
                    deleteIcon={<DeleteIcon />}
                    variant="outlined"
                  />
                ))}
              </DSBox>
            </DSBox>

            <StringChipEditor
              label="Add Type"
              values={eventTypes}
              onChange={setEventTypes}
              placeholder="Enter event type"
              helperText="Add event types one at a time"
            />

            {isKlydoEvent && (
              <>
                <DSBox>
                  <DSBox
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <DSTypography variant="body2">
                      Klydos ({selectedKlydoIds.length})
                    </DSTypography>
                    <DSPrimaryButton
                      variant="outlined"
                      size="small"
                      onClick={() => setShowPickDialog(true)}
                    >
                      Pick Klydos
                    </DSPrimaryButton>
                  </DSBox>

                  {isLoadingKlydos && <DSCircularProgress size={20} />}

                  {resolvedKlydos.length > 0 && (
                    <DSGrid container spacing={1}>
                      {resolvedKlydos.map((klydo) => (
                        <DSGrid item xs={4} key={klydo.id}>
                          <DSBox
                            onClick={() => onKlydoClick?.(klydo)}
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              p: 1,
                              position: "relative",
                              cursor: onKlydoClick ? "pointer" : "default",
                            }}
                          >
                            <DSIconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedKlydoIds((prev) =>
                                  prev.filter((id) => id !== klydo.id),
                                );
                              }}
                              sx={{
                                position: "absolute",
                                top: 0,
                                right: 0,
                                p: 0.25,
                              }}
                            >
                              <Close sx={{ fontSize: 14 }} />
                            </DSIconButton>
                            <DSBox
                              sx={{
                                borderRadius: "50%",
                                width: 48,
                                height: 48,
                                overflow: "hidden",
                              }}
                            >
                              {klydo.loopUrl ? (
                                <KlydoLoopUrl loopUrl={klydo.loopUrl} />
                              ) : (
                                <DSAvatar
                                  sx={{ width: "100%", height: "100%" }}
                                >
                                  {klydo.name?.charAt(0) || "K"}
                                </DSAvatar>
                              )}
                            </DSBox>
                            <DSTypography
                              variant="caption"
                              sx={{ mt: 0.5, textAlign: "center" }}
                            >
                              {klydo.name}
                            </DSTypography>
                          </DSBox>
                        </DSGrid>
                      ))}
                    </DSGrid>
                  )}
                </DSBox>

                <KlydoPickMethodSelect
                  value={pickMethod}
                  onChange={setPickMethod}
                />

                <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <DSTypography>Override other events</DSTypography>
                  <DSSwitch
                    checked={overrideOtherEvents}
                    onChange={() => setOverrideOtherEvents((prev) => !prev)}
                  />
                </DSBox>
              </>
            )}

            <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DSTypography>Ignore Event</DSTypography>
              <DSSwitch
                checked={ignoreEvent}
                onChange={() => setIgnoreEvent((prev) => !prev)}
              />
            </DSBox>
          </DSBox>
        </DSDialogContent>
        <DSDialogActions>
          <DSSecondaryButton onClick={onClose} disabled={isSaving}>
            Cancel
          </DSSecondaryButton>
          <DSPrimaryButton onClick={handleSave} disabled={isSaving || !isValid}>
            {isSaving ? <DSCircularProgress size={20} /> : "Save"}
          </DSPrimaryButton>
        </DSDialogActions>
      </DSDialog>

      {showPickDialog && (
        <PublicKlydosPickDialog
          onClose={() => setShowPickDialog(false)}
          selectedKlydos={selectedKlydoIds}
          onPickKlydos={setSelectedKlydoIds}
        />
      )}
    </>
  );
};

export default UnifiedCalendarEventModal;
